<template>
  <div class="quick-register-step2">
    <div class="header-section">
      <h1 class="main-title">
        Quase lá! <span class="highlight">Defina sua senha</span>
      </h1>
      <p class="subtitle">
        Seus dados de pagamento foram validados com sucesso.
      </p>
    </div>

    <form @submit.prevent="handleSubmit" class="register-form">
      <section class="form-section">
        <h2 class="section-title">Crie sua senha de acesso</h2>

        <div v-if="errorMessage" class="alert alert-danger" role="alert">
          {{ errorMessage }}
        </div>

        <!-- E-mail (readonly para password manager) -->
        <div class="mb-3">
          <label for="email" class="form-label">E-mail</label>
          <input
            id="email"
            type="email"
            class="form-control email-readonly"
            :value="quickRegisterStore.step1Data?.email"
            readonly
            autocomplete="username email"
          />
        </div>

        <!-- Senha -->
        <div class="mb-3">
          <label for="password" class="form-label">Digite sua Senha</label>
          <div class="input-group">
            <input
              id="password"
              :type="showPassword ? 'text' : 'password'"
              class="form-control"
              v-model="password"
              placeholder="Mínimo 6 caracteres"
              required
              minlength="6"
              autocomplete="new-password"
            />
            <span
              class="input-group-text"
              :class="{ active: showPassword }"
              @click="showPassword = !showPassword"
              style="cursor: pointer"
            >
              <EyeIcon v-if="!showPassword" />
              <EyeSlashIcon v-else />
            </span>
          </div>
        </div>

        <!-- Confirmação de Senha -->
        <div class="mb-3">
          <label for="password-confirmation" class="form-label"
            >Repita sua Senha</label
          >
          <div class="input-group">
            <input
              id="password-confirmation"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              class="form-control"
              v-model="passwordConfirmation"
              placeholder="Digite novamente para confirmar"
              required
              minlength="6"
              autocomplete="new-password"
            />
            <span
              class="input-group-text"
              :class="{ active: showPasswordConfirmation }"
              @click="showPasswordConfirmation = !showPasswordConfirmation"
              style="cursor: pointer"
            >
              <EyeIcon v-if="!showPassword" />
              <EyeSlashIcon v-else />
            </span>
          </div>
          <div v-if="passwordMismatch" class="text-danger small mt-1">
            As senhas não coincidem
          </div>
        </div>
      </section>

      <!-- Botão -->
      <div class="button-container">
        <button
          type="submit"
          class="btn btn-submit"
          :disabled="loading || passwordMismatch"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm me-2"
          ></span>
          {{ loading ? "FINALIZANDO..." : "FINALIZAR CADASTRO" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useMutation } from "@vue/apollo-composable";
import { gql } from "@apollo/client/core";
import { useQuickRegisterStore } from "@/stores/quickRegister";
import { useAuthStore } from "@/stores/auth";
import { getMessageFromError } from "@/libs/errors";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import EyeSlashIcon from "@/components/icons/EyeSlashIcon.vue";

const router = useRouter();
const quickRegisterStore = useQuickRegisterStore();
const authStore = useAuthStore();

const loading = ref(false);
const errorMessage = ref("");
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const password = ref("");
const passwordConfirmation = ref("");

const passwordMismatch = computed(() => {
  if (!passwordConfirmation.value) return false;
  return password.value !== passwordConfirmation.value;
});

// Mutation para definir senha inicial
const { mutate: setInitialPassword } = useMutation(
  gql`
    mutation SetInitialPassword($password: String!) {
      setInitialPassword(password: $password) {
        success
      }
    }
  `,
  { fetchPolicy: "no-cache" }
);

// Verificar se há dados da Step1
onMounted(() => {
  if (
    !quickRegisterStore.hasStep1Data ||
    !quickRegisterStore.isSubscriptionCreated
  ) {
    errorMessage.value =
      "Dados de cadastro não encontrados. Por favor, reinicie o processo.";
    setTimeout(() => {
      router.push("/auth/register");
    }, 3000);
  }
});

const handleSubmit = async () => {
  errorMessage.value = "";

  if (!quickRegisterStore.step1Data) {
    errorMessage.value =
      "Dados de cadastro não encontrados. Por favor, reinicie o processo.";
    return;
  }

  if (passwordMismatch.value) {
    errorMessage.value = "As senhas não coincidem. Por favor, verifique.";
    return;
  }

  if (!password.value || password.value.length < 6) {
    errorMessage.value = "A senha deve ter no mínimo 6 caracteres.";
    return;
  }

  loading.value = true;

  try {
    const step1Data = quickRegisterStore.step1Data;

    // 1. Definir senha inicial (usuário já foi criado no Step 1)
    await setInitialPassword({
      password: password.value,
    });

    // 2. Fazer login para obter um novo token (agora com senha)
    await authStore.login(step1Data.email, password.value);

    // 3. Limpar dados do quick register
    quickRegisterStore.clearData();

    // 4. Redirecionar para página de sucesso
    router.push("/auth/register/success");
  } catch (err) {
    errorMessage.value = getMessageFromError(err);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.quick-register-step2 {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 2rem 1rem;
}

.quick-register-step2 > * {
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
}

.header-section {
  text-align: center;
  margin-bottom: 2.5rem;
  background: #16161d;
  padding: 1.5rem;
  border-radius: 12px;
}

.main-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #fff;
}

.main-title .highlight {
  color: #a6c715;
}

.subtitle {
  font-size: 0.95rem;
  color: #fff;
  margin: 0;
}

.form-section {
  background: #ffffff;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #1a1a1a;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #333;
  font-size: 0.9rem;
}

.form-control,
.form-select {
  background: #f8f8f8;
  border: 1px solid #d0d0d0;
  color: #333;
  padding: 0.75rem;
  border-radius: 8px;
}

.form-control:focus,
.form-select:focus {
  background: #fff;
  border-color: #a6c715;
  color: #333;
  box-shadow: 0 0 0 0.2rem rgba(166, 199, 21, 0.25);
}

.form-control::placeholder {
  color: #999;
}

.input-group-text {
  background: #f8f8f8;
  border: 1px solid #d0d0d0;
  color: #666;
}

.input-group-text.active {
  background: #a6c715;
  color: #fff;
  border-color: #a6c715;
}

.email-readonly {
  background: #e9ecef !important;
  color: #495057;
  cursor: not-allowed;
  font-weight: 500;
}

.email-readonly:focus {
  background: #e9ecef !important;
  border-color: #d0d0d0 !important;
  box-shadow: none !important;
}

.form-check-input:checked {
  background-color: #a6c715;
  border-color: #a6c715;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.btn-submit {
  background: #a6c715;
  color: #16161d;
  font-weight: 700;
  font-size: 1.1rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-submit:hover:not(:disabled) {
  background: #8fa612;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(166, 199, 21, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.policies-agreement {
  font-size: 0.875rem;
  color: #333;
}

.policies-agreement a {
  color: #a6c715;
  text-decoration: none;
  font-weight: 500;
}

.policies-agreement a:hover {
  text-decoration: underline;
}

.alert {
  margin-bottom: 1rem;
}

.text-danger {
  color: #dc3545;
}

@media (max-width: 768px) {
  .quick-register-step2 {
    padding: 1rem 0.5rem;
  }

  .form-section {
    padding: 1.5rem 1rem;
  }

  .main-title {
    font-size: 1.5rem;
  }

  .btn-submit {
    max-width: 100%;
    font-size: 1rem;
    padding: 0.875rem 1.5rem;
  }
}

/* Breakpoint para telas muito pequenas (≤479px) */
@media (max-width: 479px) {
  .quick-register-step2 {
    padding: 0.75rem 0.5rem;
  }

  .form-section {
    padding: 1.25rem 0.75rem;
  }

  .section-title {
    font-size: 1.1rem;
  }
}
</style>
